/**
 * roadmap-chat.store.ts — conversation state for the SSI roadmap agent.
 *
 * Session-only by design (no user-preferences persistence). One Zustand store
 * so the panel can mount on any roadmap page. Each New chat / clear aborts the
 * in-flight reply, mints a conversationId, and drops prior tool history so
 * threads cannot bleed. The server is replayed the exact tool calls + results,
 * including turns that ended in a cut-off error (the write may already exist).
 *
 * Optional image attachments travel with the current turn as base64 JSON so
 * the model can see them and so create_feature_request can upload them onto
 * the new FR.
 */

import { create } from 'zustand';

import { useAuthStore } from '@/stores/auth.store';

import { takeReplayableHistory } from '../lib/roadmap-agent-history';

/** Tool/user/assistant turns sent back to the server for exact replay. */
export type RoadmapChatHistoryMessage =
  | { role: 'user'; content: string }
  | { role: 'assistant'; content: string; toolCalls?: Array<{ id: string; name: string; input: Record<string, unknown> }> }
  | { role: 'tool'; toolCallId: string; content: string };

/** One SSE event from the agent (mirrors `RoadmapAgentEvent` on the server). */
type AgentStreamEvent =
  | { type: 'delta'; text: string }
  | { type: 'tool'; name: string }
  | { type: 'turn'; messages: RoadmapChatHistoryMessage[] }
  | { type: 'error'; message: string }
  | { type: 'done' };

export interface RoadmapChatImage {
  id: string;
  file: File;
  /** Object URL for local preview (revoked on remove/send). */
  previewUrl: string;
}

export interface RoadmapChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  /** Local preview URLs shown on the user bubble (not sent again in history). */
  imagePreviewUrls?: string[];
  /** Waiting for the first token (or a tool round-trip). */
  isLoading?: boolean;
  /** Friendly label for the tool currently running ("Reading the roadmap…"). */
  activity?: string;
  error?: string;
}

/** Trailing exact history sent back to the server (user + assistant + tools). */
const HISTORY_MESSAGES = 80;
const MAX_HISTORY_CONTENT = 20_000;
const MAX_CHAT_IMAGES = 3;
const MAX_IMAGE_BYTES = 2.5 * 1024 * 1024;

function newConversationId(): string {
  return crypto.randomUUID();
}

function clipHistoryContent(content: string): string {
  if (content.length <= MAX_HISTORY_CONTENT) return content;
  return `${content.slice(0, MAX_HISTORY_CONTENT)}… [truncated]`;
}

const TOOL_ACTIVITY_LABELS: Record<string, string> = {
  list_features: 'Reading the roadmap…',
  list_milestones: 'Checking milestones…',
  list_requests: 'Searching feature requests…',
  check_duplicates: 'Looking for similar tickets…',
  list_taxonomy: 'Checking categories…',
  get_feature: 'Opening a feature…',
  get_request: 'Opening a feature request…',
  list_comments: 'Reading comments…',
  list_attachments: 'Checking images…',
  watch_request: 'Watching the ticket…',
  create_feature_request: 'Submitting the feature request…',
  add_comment: 'Adding a comment…',
  update_comment: 'Editing a comment…',
  delete_comment: 'Removing a comment…',
  update_feature: 'Updating the feature…',
  update_request: 'Updating the request…',
  promote_request: 'Promoting to the roadmap…',
  demote_request: 'Demoting from the roadmap…',
};

let messageCounter = 0;
function nextId(): string {
  return `rmsg_${Date.now()}_${++messageCounter}`;
}

/** Aborts the in-flight chat POST when Plus starts a new conversation. */
let inFlightAbort: AbortController | null = null;

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Failed to read image'));
        return;
      }
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read image'));
    reader.readAsDataURL(file);
  });
}

interface RoadmapChatState {
  conversationId: string;
  messages: RoadmapChatMessage[];
  /** Exact tool history for the current conversationId only. */
  transcript: RoadmapChatHistoryMessage[];
  isLoading: boolean;
  isOpen: boolean;
  /** WS10 — Submit request opened the agent as a filing flow. */
  submitIntent: { pathname: string } | null;
  pendingImages: RoadmapChatImage[];
  setOpen: (open: boolean) => void;
  openSubmit: (context: { pathname: string }) => void;
  addPendingImages: (files: File[]) => string | null;
  removePendingImage: (id: string) => void;
  sendMessage: (text: string) => Promise<void>;
  clear: () => void;
}

export const useRoadmapChatStore = create<RoadmapChatState>((set, get) => ({
  conversationId: newConversationId(),
  messages: [],
  transcript: [],
  isLoading: false,
  isOpen: false,
  submitIntent: null,
  pendingImages: [],

  setOpen: (open) => set({ isOpen: open }),

  openSubmit: (context) => {
    const current = get().submitIntent;
    if (!current) {
      get().clear();
    }
    set({ isOpen: true, submitIntent: { pathname: context.pathname } });
  },

  addPendingImages: (files) => {
    const images = files.filter((f) => f.type.startsWith('image/'));
    if (images.length === 0) return 'Only PNG, JPEG, WebP, or GIF images are supported.';
    const current = get().pendingImages;
    if (current.length + images.length > MAX_CHAT_IMAGES) {
      return `You can attach up to ${MAX_CHAT_IMAGES} images per message.`;
    }
    for (const file of images) {
      if (file.size > MAX_IMAGE_BYTES) {
        return `'${file.name}' exceeds the 2.5 MB limit.`;
      }
    }
    const next = images.map((file) => ({
      id: nextId(),
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    set({ pendingImages: [...current, ...next] });
    return null;
  },

  removePendingImage: (id) => {
    const target = get().pendingImages.find((img) => img.id === id);
    if (target) URL.revokeObjectURL(target.previewUrl);
    set({ pendingImages: get().pendingImages.filter((img) => img.id !== id) });
  },

  clear: () => {
    inFlightAbort?.abort();
    inFlightAbort = null;
    for (const img of get().pendingImages) URL.revokeObjectURL(img.previewUrl);
    set({
      conversationId: newConversationId(),
      messages: [],
      transcript: [],
      pendingImages: [],
      isLoading: false,
      submitIntent: null,
    });
  },

  sendMessage: async (text) => {
    const trimmed = text.trim();
    const pending = get().pendingImages;
    if ((!trimmed && pending.length === 0) || get().isLoading) return;

    const sentConversationId = get().conversationId;
    const ac = new AbortController();
    inFlightAbort = ac;

    const previewUrls = pending.map((p) => p.previewUrl);
    const userMsg: RoadmapChatMessage = {
      id: nextId(),
      role: 'user',
      content: trimmed || '(attached images)',
      imagePreviewUrls: previewUrls.length ? previewUrls : undefined,
    };
    const assistantMsg: RoadmapChatMessage = {
      id: nextId(),
      role: 'assistant',
      content: '',
      isLoading: true,
    };

    const prior = get().messages;
    set({
      messages: [...prior, userMsg, assistantMsg],
      isLoading: true,
      pendingImages: [],
    });

    const userText = trimmed || 'Please review the attached images.';
    const history = takeReplayableHistory(get().transcript, HISTORY_MESSAGES).map((m) =>
      m.role === 'tool'
        ? { ...m, content: clipHistoryContent(m.content) }
        : { ...m, content: clipHistoryContent(m.content) },
    );

    let imagesPayload:
      | Array<{ fileName: string; mimeType: string; dataBase64: string }>
      | undefined;
    let answer = '';
    let activity: string | undefined;
    let errorText: string | undefined;
    let turnMessages: RoadmapChatHistoryMessage[] = [];

    const stillThisConversation = () => get().conversationId === sentConversationId;

    const apply = () => {
      if (!stillThisConversation()) return;
      set((state) => ({
        messages: state.messages.map((m) =>
          m.id === assistantMsg.id
            ? {
                ...m,
                content: answer,
                isLoading: answer.length === 0 && !errorText,
                activity: answer.length === 0 ? activity : undefined,
                error: errorText,
              }
            : m,
        ),
      }));
    };

    const persistTurn = () => {
      if (!stillThisConversation() || turnMessages.length === 0) return;
      set((state) => ({
        transcript: takeReplayableHistory(
          [
            ...state.transcript,
            { role: 'user' as const, content: userText },
            ...turnMessages,
          ],
          HISTORY_MESSAGES,
        ),
      }));
    };

    const handleEvent = (event: AgentStreamEvent) => {
      if (event.type === 'delta') {
        answer += event.text;
        activity = undefined;
      } else if (event.type === 'tool') {
        activity = TOOL_ACTIVITY_LABELS[event.name] ?? 'Working…';
      } else if (event.type === 'turn') {
        turnMessages = event.messages;
      } else if (event.type === 'error') {
        errorText = event.message;
      }
      apply();
    };

    try {
      try {
        if (pending.length > 0) {
          imagesPayload = await Promise.all(
            pending.map(async (img) => ({
              fileName: img.file.name,
              mimeType: img.file.type || 'image/png',
              dataBase64: await fileToBase64(img.file),
            })),
          );
        }
      } catch {
        if (!stillThisConversation()) return;
        set((state) => ({
          isLoading: false,
          messages: state.messages.map((m) =>
            m.id === assistantMsg.id
              ? { ...m, isLoading: false, error: 'Failed to read one of the attached images.' }
              : m,
          ),
        }));
        return;
      }

      if (!stillThisConversation()) return;

      const token = useAuthStore.getState().accessToken;
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
      };
      if (token) headers.Authorization = `Bearer ${token}`;
      const apiSecret = import.meta.env.VITE_API_SECRET;
      if (apiSecret) headers['x-api-secret'] = apiSecret;

      const res = await fetch('/api/roadmap/agent/chat', {
        method: 'POST',
        headers,
        credentials: 'include',
        signal: ac.signal,
        body: JSON.stringify({
          message: userText,
          conversationId: sentConversationId,
          conversationHistory: history,
          ...(imagesPayload ? { images: imagesPayload } : {}),
          ...(get().submitIntent
            ? { intent: 'submit', pagePath: get().submitIntent?.pathname }
            : {}),
        }),
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      const handleFrame = (frame: string) => {
        const dataLine = frame.split('\n').find((l) => l.startsWith('data:'));
        if (!dataLine) return;
        const payload = dataLine.slice('data:'.length).trim();
        if (!payload) return;
        try {
          handleEvent(JSON.parse(payload) as AgentStreamEvent);
        } catch {
          /* skip malformed frames */
        }
      };

      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let sep: number;
        while ((sep = buffer.indexOf('\n\n')) !== -1) {
          const frame = buffer.slice(0, sep);
          buffer = buffer.slice(sep + 2);
          handleFrame(frame);
        }
      }
      if (buffer.trim()) handleFrame(buffer);

      if (!answer && !errorText) {
        answer = "I wasn't able to generate an answer. Please try rephrasing.";
      }
      apply();
      persistTurn();
    } catch (err) {
      if (ac.signal.aborted || !stillThisConversation()) return;
      const name = err instanceof Error ? err.name : '';
      if (name === 'AbortError') return;
      errorText = 'The roadmap agent is unreachable. Please try again.';
      apply();
    } finally {
      if (inFlightAbort === ac) inFlightAbort = null;
      if (stillThisConversation()) {
        set({ isLoading: false });
      }
    }
  },
}));
